import { useState, useMemo, forwardRef, InputHTMLAttributes } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLanguage,
  faExclamationCircle,
} from "@fortawesome/pro-light-svg-icons";
import TranslationPopover from "./TranslationPopover";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";

/**
 * Props for the TranslatableTextInput component
 */
interface TranslatableTextInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement>,
  "value" | "onChange" | "children"
> {
  /** The current value of the input field (typically the default language value) */
  value: string;

  /** Callback when the main input value changes */
  onChange?: (value: string) => void;

  /**
   * The translations object containing all language translations
   * Format: { "en": { "fieldName": "value" }, "de": { "fieldName": "value" } }
   */
  translationsValue: Record<string, Record<string, string>>;

  /**
   * Callback when translations are updated.
   * Consumers should also sync the current locale value back to the main field if needed.
   */
  onTranslationsChange?: (
    translations: Record<string, Record<string, string>>,
  ) => void;

  /**
   * Unified callback that provides both the main value and translations atomically.
   * When provided, handles bidirectional sync internally:
   * - Main input changes sync to translations[defaultLocale][field]
   * - Popover changes sync translations[defaultLocale][field] back to the main value
   * Replaces the need for separate onChange + onTranslationsChange with manual sync logic.
   */
  onValueChange?: (
    value: string,
    translations: Record<string, Record<string, string>>,
  ) => void;

  /** The field name being translated (e.g., "text", "title", "description") */
  field: string;

  /** Whether the input is disabled */
  disabled?: boolean;

  /** Whether to render a textarea instead of input (for multiline content) */
  multiline?: boolean;

  /** Number of rows for textarea when multiline is true */
  rows?: number;

  /** Maximum character length for the input/textarea (forwarded to popover inputs too) */
  maxLength?: number;

  /** Side where the popover should appear (default: "left") */
  popoverSide?: "top" | "right" | "bottom" | "left";

  /** Alignment of the popover relative to the trigger (default: "start") */
  popoverAlign?: "start" | "center" | "end";

  /**
   * Class on the flex wrapper that holds input + translate toggle.
   * Use this when the caller lays the whole widget out inside a larger grid/flex
   * (e.g. survey options) so the wrapper can participate in that layout.
   */
  containerClassName?: string;
}

/**
 * Text input component with integrated multilingual translation support
 *
 * Features:
 * - Shows a globe icon button next to the input that opens a translation popover
 * - Icon color indicates translation status:
 *   - Green: All configured languages have translations
 *   - Red with exclamation: One or more translations are missing
 * - Automatically hidden when multilingualism is disabled
 * - Supports all standard HTML input attributes
 *
 * Architecture - Colocated Translations:
 * The component follows the colocated translations pattern where:
 * - The main `value` prop contains the default language content
 * - The `translationsValue` contains translations for other languages
 *
 * @example Using onValueChange (recommended — handles sync automatically)
 * ```tsx
 * <TranslatableTextInput
 *   value={item.text}
 *   translationsValue={item.translations || {}}
 *   onValueChange={(text, translations) => setItem({ ...item, text, translations })}
 *   field="text"
 *   className="form-control"
 * />
 * ```
 *
 * @example Using separate onChange + onTranslationsChange (legacy)
 * ```tsx
 * <TranslatableTextInput
 *   value={item.text}
 *   onChange={(text) => setItem({ ...item, text })}
 *   translationsValue={item.translations || {}}
 *   onTranslationsChange={(translations) => setItem({ ...item, translations })}
 *   field="text"
 *   className="form-control"
 * />
 * ```
 */
const TranslatableTextInput = forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  TranslatableTextInputProps
>(function TranslatableTextInput(
  {
    value,
    onChange,
    translationsValue,
    onTranslationsChange,
    onValueChange,
    field,
    disabled = false,
    className = "form-control",
    multiline = false,
    rows = 4,
    maxLength,
    popoverSide = "left",
    popoverAlign = "start",
    containerClassName,
    ...inputProps
  },
  ref,
) {
  const [isOpen, setIsOpen] = useState(false);

  const defaultLocale = Preload.current_network?.language?.split("_")[0];

  // Check if multilingualism is enabled
  const multilingualismEnabled =
    Preload.current_network?.config.multilingualism.enabled;

  const availableLanguages = useMemo(() => {
    return (
      Preload.current_network?.config.multilingualism.available_languages || []
    );
  }, []);

  // Check for missing translations
  const hasMissingTranslations = useMemo(() => {
    if (!translationsValue) return false;

    return availableLanguages.some((lang) => {
      const translation = translationsValue[lang]?.[field];
      return !translation || translation.trim() === "";
    });
  }, [translationsValue, availableLanguages, field]);

  const handleInputChange = (newText: string) => {
    if (onValueChange) {
      const syncedTranslations =
        defaultLocale && multilingualismEnabled
          ? {
              ...translationsValue,
              [defaultLocale]: {
                ...(translationsValue[defaultLocale] || {}),
                [field]: newText,
              },
            }
          : translationsValue;
      onValueChange(newText, syncedTranslations);
    } else {
      onChange?.(newText);
    }
  };

  const handleTranslationsChange = (
    translations: Record<string, Record<string, string>>,
  ) => {
    if (onValueChange) {
      const syncedValue =
        defaultLocale && translations[defaultLocale]?.[field] !== undefined
          ? translations[defaultLocale][field]
          : value;
      onValueChange(syncedValue, translations);
    } else {
      onTranslationsChange?.(translations);
    }
  };

  const inputElement = multiline ? (
    <textarea
      ref={ref as React.Ref<HTMLTextAreaElement>}
      className={className}
      value={value}
      onChange={(e) => handleInputChange(e.target.value)}
      disabled={disabled}
      rows={rows}
      maxLength={maxLength}
      {...(inputProps as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
    />
  ) : (
    <input
      ref={ref as React.Ref<HTMLInputElement>}
      className={className}
      value={value}
      onChange={(e) => handleInputChange(e.target.value)}
      disabled={disabled}
      maxLength={maxLength}
      {...(inputProps as React.InputHTMLAttributes<HTMLInputElement>)}
    />
  );

  if (!multilingualismEnabled) {
    return inputElement;
  }

  return (
    <div className={`flex items-center gap-2 ${containerClassName ?? ""}`}>
      {inputElement}

      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="btn btn-sm btn-link p-1"
            data-testid="translation-toggle"
            disabled={disabled}
            style={{
              position: "relative",
              lineHeight: 1,
              opacity: disabled ? 0.5 : 1,
              cursor: disabled ? "not-allowed" : "pointer",
            }}
          >
            <FontAwesomeIcon
              icon={faLanguage}
              className={
                hasMissingTranslations ? "text-danger" : "text-success"
              }
              size="lg"
            />
            {hasMissingTranslations && (
              <FontAwesomeIcon
                icon={faExclamationCircle}
                className="text-danger"
                style={{
                  position: "absolute",
                  top: "-4px",
                  right: "-4px",
                  fontSize: "0.7em",
                  backgroundColor: "white",
                  borderRadius: "50%",
                }}
              />
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent
          align={popoverAlign}
          side={popoverSide}
          className="w-auto p-4"
        >
          <TranslationPopover
            field={field}
            translationsValue={translationsValue}
            onTranslationsChange={handleTranslationsChange}
            onClose={() => setIsOpen(false)}
            multiline={multiline}
            rows={rows}
            maxLength={maxLength}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
});

export default TranslatableTextInput;
